import { raRowRatings } from '@felix/domain';
import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { ltUtcText, zonedStamp } from '../../inputs/lt-utc.js';
import { TOKENS } from '../../tokens.js';
import type { ReportBrand } from '../voyage/source.js';
import type { RaVersionView } from './library/types.js';
import type { RaRegisterVersion } from './register/types.js';
import type { RaVerificationDetail } from './verification/types.js';

/**
 * Risk modülünün PDF'leri (K7; ADR 0095 §2; ADR 0057 §7 dili): yayımlı RA sürümü (içerik, matris
 * sürümü, onay izi ve gemideki okundu onayı) ve operasyon doğrulama kaydı (cevaplar, yetki,
 * olaylar). A4; şirket adı ve logo; her sayfada basım zamanı ve sayfa no. Saf işlevler: gemi ve
 * ofis aynı tanımı kullanır; pdfmake `renderPdf` ile basılır.
 */

/** Çevirici: i18next `t`. */
export type RaPdfT = (key: string, options?: Record<string, unknown>) => string;

const FONT_SIZE = 8.5;
const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};
const FACTS: CustomTableLayout = { ...LINES, fillColor: () => null, hLineWidth: () => 0.4 };

/** Logo yalnız PNG ya da JPEG veri adresiyse basılır (pdfmake; ADR 0057 §7). */
function logoOf(brand: ReportBrand | null): string | null {
  const logo = brand?.logo ?? null;
  return logo && /^data:image\/(png|jpeg);base64,/.test(logo) ? logo : null;
}

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : '—');

/** `YYYY-MM-DD` → `DD.MM.YYYY`. */
const day = (value: string | null | undefined) =>
  value && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`
    : '—';

/** Ortak yapı taşları: başlık, iki sütunlu bilgi tablosu, tablo, belge çerçevesi. */
function kit(t: RaPdfT) {
  const heading = (value: string): Content => ({ text: value, style: 'section' });
  const facts = (pairs: [string, string][]): Content => {
    const body: TableCell[][] = [];
    for (let i = 0; i < pairs.length; i += 2) {
      const [a, b] = [pairs[i], pairs[i + 1]];
      body.push([
        { text: a?.[0] ?? '', style: 'label' },
        { text: a?.[1] ?? '' },
        { text: b?.[0] ?? '', style: 'label' },
        { text: b?.[1] ?? '' },
      ]);
    }
    return { table: { widths: [95, '*', 95, '*'], body }, layout: FACTS };
  };
  const table = (head: string[], body: string[][], widths: (string | number)[]): Content =>
    body.length === 0
      ? { text: t('riskDashboard.pdf.none'), style: 'muted' }
      : {
          table: {
            headerRows: 1,
            widths,
            body: [head.map((h) => ({ text: h, bold: true })), ...body],
          },
          layout: LINES,
        };
  const header = (input: {
    brand: ReportBrand | null;
    title: string;
    subtitle: string;
    number: string;
    stage: string;
    mark?: string | null;
  }): Content => {
    const logo = logoOf(input.brand);
    return {
      columns: [
        ...(logo ? [{ image: logo, fit: [48, 48] as [number, number], width: 56 }] : []),
        {
          width: '*',
          stack: [
            { text: input.brand?.name ?? '', style: 'company' },
            { text: input.title, style: 'title' },
            { text: input.subtitle, style: 'muted' },
            { text: input.number, style: 'muted' },
          ],
        },
        {
          width: 'auto',
          stack: [
            { text: input.stage, style: 'stage', alignment: 'right' as const },
            ...(input.mark
              ? [{ text: input.mark, style: 'mark', alignment: 'right' as const }]
              : []),
          ],
        },
      ],
      columnGap: 10,
      margin: [0, 0, 0, 8],
    };
  };
  const document = (input: {
    title: string;
    number: string;
    subject: string;
    printedAt: string;
    orientation: 'portrait' | 'landscape';
    content: Content[];
  }): TDocumentDefinitions => {
    const printed = t('riskDashboard.pdf.printed', { at: `${zonedStamp(input.printedAt)} UTC` });
    return {
      pageSize: 'A4',
      pageOrientation: input.orientation,
      pageMargins: [32, 32, 32, 44],
      defaultStyle: { font: 'Roboto', fontSize: FONT_SIZE, color: TOKENS['navy-900'] },
      info: {
        title: `${input.title} ${input.number}`,
        subject: input.subject,
        creator: 'Felixmate',
      },
      styles: {
        company: { fontSize: 10, bold: true, color: TOKENS['text-secondary'] },
        title: { fontSize: 15, bold: true, margin: [0, 2, 0, 2] },
        section: { fontSize: 10.5, bold: true, margin: [0, 10, 0, 4] },
        label: { color: TOKENS['text-secondary'] },
        muted: { color: TOKENS['text-secondary'], fontSize: 8 },
        stage: { bold: true, fontSize: 10 },
        mark: { bold: true, color: TOKENS['danger-fg'], fontSize: 9 },
      },
      content: input.content,
      footer: (page: number, pages: number) => ({
        columns: [
          {
            text: `${input.number} · ${printed} · Felixmate`,
            fontSize: 7,
            color: TOKENS['text-secondary'],
          },
          {
            text: t('riskDashboard.pdf.page', { page, pages }),
            alignment: 'right',
            fontSize: 7,
            color: TOKENS['text-secondary'],
          },
        ],
        margin: [32, 12, 32, 0],
      }),
    };
  };
  return { heading, facts, table, header, document };
}

export interface RaVersionPdfInput {
  version: RaVersionView;
  /** Gemideki register satırı (okundu onayı ve dayanak); kütüphaneden basılınca yok. */
  register?: RaRegisterVersion | null;
  vessel?: { name: string } | null;
  brand: ReportBrand | null;
  printedAt: string;
}

/**
 * Yayımlı RA sürümünün PDF'i (R24; FD §12 "immutable version"): kimlik, revizyon, yayım (kim, ne
 * zaman), matris sürümü, içerik hash'i; genel bilgiler; risk satırları (adım, tehlike, sonuç,
 * mevcut ve ek önlem, ilk ve hedef puan); faaliyete özel maddeler; onay izi (bulutta) ve gemideki
 * okundu onayı. A4 yatay (geniş risk tablosu).
 */
export function raVersionPdf(t: RaPdfT, input: RaVersionPdfInput): TDocumentDefinitions {
  const { version } = input;
  const p = (key: string, options?: Record<string, unknown>) =>
    t(`riskDashboard.pdf.${key}`, options);
  const { heading, facts, table, header, document } = kit(t);
  const g = version.content.general;
  const level = (rating: { level: string; score: number } | null) =>
    rating ? `${t(`risk.levels.${rating.level}`)} (${rating.score})` : '—';
  const content: Content[] = [
    header({
      brand: input.brand,
      title: p('version.title'),
      subtitle: `${version.code} · ${version.title}`,
      number: p('version.revision', { revision: version.revision }),
      stage: p('version.published'),
      mark: version.migrated ? p('version.migrated') : null,
    }),
    heading(p('version.general')),
    facts([
      [p('fields.code'), version.code],
      [p('fields.revision'), version.revision],
      [p('fields.title'), version.title],
      [p('fields.activity'), [g.activityCode, g.activityName].filter(Boolean).join(' · ') || '—'],
      [
        p('fields.type'),
        t(`riskMaster.assessmentTypes.${g.assessmentType}`, { defaultValue: g.assessmentType }),
      ],
      [p('fields.groups'), g.vesselGroups.join(', ')],
      [p('fields.location'), text(g.location)],
      [p('fields.team'), text(g.team)],
      [p('fields.prepared'), day(g.preparedDay)],
      [p('fields.review'), day(g.reviewDay)],
      [p('fields.procedures'), text(g.procedures)],
      [p('fields.scope'), text(g.scope)],
    ]),
    heading(p('version.publication')),
    facts([
      [p('fields.publishedAt'), `${zonedStamp(version.publishedAt)} UTC`],
      [p('fields.publishedBy'), text(version.publishedByName)],
      [
        p('fields.matrix'),
        version.matrix
          ? p('fields.matrixVersion', { version: version.matrix.version })
          : p('legacy'),
      ],
      [p('fields.hash'), version.contentHash],
    ]),
    heading(p('version.rows')),
    table(
      [
        p('columns.no'),
        p('columns.step'),
        p('columns.hazard'),
        p('columns.consequence'),
        p('columns.existing'),
        p('columns.initial'),
        p('columns.additional'),
        p('columns.residual'),
      ],
      version.content.rows.map((row, index) => {
        const r = raRowRatings(row, version.matrixDefinition);
        return [
          String(index + 1),
          row.step,
          row.hazard,
          text(row.consequence),
          row.existingControls
            .map(
              (c) => `• ${c.measure}${c.requirement === 'mandatory' ? ` (${p('mandatory')})` : ''}`,
            )
            .join('\n') || '—',
          level(r.inherent),
          row.additionalControls.map((c) => `• ${c.measure}`).join('\n') || '—',
          level(r.residual),
        ];
      }),
      [16, 70, 80, 90, '*', 58, '*', 58],
    ),
  ];
  const checks = version.content.checks ?? [];
  if (checks.length > 0) {
    content.push(
      heading(p('version.checks')),
      table(
        [p('columns.no'), p('columns.check'), p('columns.na')],
        checks.map((c, i) => [String(i + 1), c.text, c.naAllowed ? p('yes') : p('no')]),
        [16, '*', 60],
      ),
    );
  }
  content.push(heading(p('version.trail')));
  if (version.trail) {
    content.push(
      table(
        [p('columns.at'), p('columns.event'), p('columns.by'), p('columns.details')],
        version.trail.map((e) => [
          `${zonedStamp(e.at)} UTC`,
          t(`riskLibrary.events.${e.kind}`, {
            role: e.acceptor?.toUpperCase() ?? '',
            defaultValue: e.kind,
          }),
          text(e.byName),
          [e.reason, e.sameActor ? p('sameActor') : null].filter(Boolean).join(' · ') || '—',
        ]),
        [110, 110, 140, '*'],
      ),
    );
  } else {
    content.push({ text: p('trailOffice'), style: 'muted' });
  }
  if (input.register) {
    const ack = input.register.acknowledgedBy;
    content.push(
      heading(p('version.acknowledgement', { vessel: input.vessel?.name ?? '' })),
      facts([
        [
          p('fields.acknowledgement'),
          t(`riskRegister.acknowledgement.${input.register.acknowledgement}`),
        ],
        [p('fields.acknowledgedBy'), ack ? `${text(ack.byName)} (${text(ack.roleName)})` : '—'],
        [p('fields.acknowledgedAt'), ack ? ltUtcText(ack.at, ack.tzOffsetMin) : '—'],
        [p('fields.basis'), t(`riskRegister.basis.${input.register.basis}`, { group: '' })],
      ]),
    );
  }
  return document({
    title: p('version.title'),
    number: `${version.code} Rev. ${version.revision}`,
    subject: input.vessel?.name ?? version.title,
    printedAt: input.printedAt,
    orientation: 'landscape',
    content,
  });
}

export interface RaVerificationPdfInput {
  detail: RaVerificationDetail;
  vessel: { name: string };
  brand: ReportBrand | null;
  printedAt: string;
}

/**
 * Operasyon doğrulama kaydının PDF'i (R18–R22; FD §11 karar alanları): operasyon, RA sürümü ve
 * checklist hash'i, kalan risk ve kaptan kabulü; maddeler ve geçerli cevaplar (kim, ne zaman, gemi
 * saati); önceki cevaplar; yetki ve olay izi (rol, gerekçe, geçerlilik, aynı kişi); brifing.
 */
export function raVerificationPdf(t: RaPdfT, input: RaVerificationPdfInput): TDocumentDefinitions {
  const { detail } = input;
  const p = (key: string, options?: Record<string, unknown>) =>
    t(`riskDashboard.pdf.${key}`, options);
  const v = (key: string, options?: Record<string, unknown>) =>
    t(`riskVerification.${key}`, options);
  const { heading, facts, table, header, document } = kit(t);
  const offset = detail.tzOffsetMin;
  const time = (iso: string | null | undefined, off = offset) => (iso ? ltUtcText(iso, off) : '—');
  const stamp = (iso: string, off: number) => `${zonedStamp(iso, off)} LT · ${zonedStamp(iso)} UTC`;
  const label = (item: { key: string; source: string; text: string | null }) =>
    item.source === 'common' ? v(`common.${item.key.slice('common:'.length)}`) : text(item.text);
  const answer = (value: string) => v(`answers.${value}`, { defaultValue: value });
  const content: Content[] = [
    header({
      brand: input.brand,
      title: p('verification.title'),
      subtitle: `${detail.operation} · ${input.vessel.name}`,
      number: detail.number,
      stage: v(`statuses.${detail.status}`),
      mark: detail.risk.blocked ? p('verification.critical') : null,
    }),
    heading(p('verification.operation')),
    facts([
      [p('fields.number'), detail.number],
      [p('fields.vessel'), input.vessel.name],
      [p('fields.operation'), detail.operation],
      [p('fields.location'), text(detail.location)],
      [p('fields.plannedStart'), time(detail.plannedStart)],
      [p('fields.plannedEnd'), time(detail.plannedEnd)],
      [p('fields.ra'), `${detail.code} Rev. ${detail.revision} · ${detail.title}`],
      [p('fields.round'), String(detail.round)],
      [
        p('fields.residual'),
        detail.risk.residual
          ? `${t(`risk.levels.${detail.risk.residual.level}`)} (${detail.risk.residual.score})`
          : '—',
      ],
      [p('fields.masterRequired'), detail.risk.masterRequired ? p('yes') : p('no')],
      [p('fields.checklistHash'), detail.checklistHash],
      [p('fields.raHash'), detail.checklist.contentHash],
      [p('fields.openedBy'), `${text(detail.createdByName)} · ${time(detail.createdAt)}`],
      [
        p('fields.authorization'),
        detail.authorizedByName
          ? p('verification.authorizedBy', {
              name: detail.authorizedByName,
              until: time(detail.validUntil),
            })
          : '—',
      ],
    ]),
    heading(p('verification.checklist', { answered: detail.answered, total: detail.total })),
    table(
      [
        p('columns.no'),
        p('columns.check'),
        p('columns.answer'),
        p('columns.comment'),
        p('columns.evidence'),
        p('columns.by'),
      ],
      detail.items.map((item, index) => [
        String(index + 1),
        [label(item), item.context ? `(${item.context})` : null].filter(Boolean).join('\n'),
        item.current ? answer(item.current.answer) : p('unanswered'),
        text(item.current?.comment),
        text(item.current?.evidence),
        item.current
          ? `${text(item.current.byName)}\n${stamp(item.current.at, item.current.tzOffsetMin)}`
          : '—',
      ]),
      [16, '*', 46, 110, 90, 120],
    ),
  ];
  const earlier = detail.items.flatMap((item) =>
    item.history.filter((h) => h.id !== item.current?.id).map((h) => ({ item, h })),
  );
  if (earlier.length > 0) {
    content.push(
      heading(p('verification.history')),
      table(
        [
          p('columns.check'),
          p('columns.round'),
          p('columns.answer'),
          p('columns.comment'),
          p('columns.by'),
        ],
        earlier.map(({ item, h }) => [
          label(item),
          String(h.round),
          answer(h.answer),
          text(h.comment),
          `${text(h.byName)}\n${stamp(h.at, h.tzOffsetMin)}`,
        ]),
        ['*', 36, 46, 130, 120],
      ),
    );
  }
  content.push(
    heading(p('verification.events')),
    table(
      [p('columns.at'), p('columns.event'), p('columns.by'), p('columns.details')],
      detail.events.map((e) => [
        stamp(e.at, e.tzOffsetMin),
        v(`events.${e.kind}`, { defaultValue: e.kind }),
        [text(e.byName), e.roleName].filter(Boolean).join('\n'),
        [
          e.outcome ? v(`statuses.${e.outcome}`, { defaultValue: e.outcome }) : null,
          e.reason,
          e.validUntil ? p('validUntil', { at: time(e.validUntil, e.tzOffsetMin) }) : null,
          e.residualBand ? p('band', { band: e.residualBand }) : null,
          e.masterAcceptance ? p('masterAcceptance') : null,
          e.sameActor ? p('sameActor') : null,
          e.reauthenticated ? p('reauthenticated') : null,
        ]
          .filter(Boolean)
          .join(' · ') || '—',
      ]),
      [118, 90, 110, '*'],
    ),
  );
  content.push(heading(p('verification.briefed')));
  content.push(
    detail.briefed.length === 0
      ? { text: p('none'), style: 'muted' }
      : { ul: detail.briefed.map((b) => `${text(b.name)} · ${time(b.at)}`) },
  );
  content.push({
    text: p('verification.note'),
    style: 'muted',
    margin: [0, 8, 0, 0],
  });
  return document({
    title: p('verification.title'),
    number: detail.number,
    subject: input.vessel.name,
    printedAt: input.printedAt,
    orientation: 'portrait',
    content,
  });
}

/** Dosya adı: "OV-2026-001 Operational Verification.pdf". */
export function raPdfFileName(number: string, title: string): string {
  return `${`${number} ${title}`.replace(/[^A-Za-z0-9 ._-]+/g, '-')}.pdf`;
}
